import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth, API } from '@/App';
import { toast } from 'sonner';

// Brand logo URL (transparent background)
const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_17ae6403-2082-451f-978f-8e823488d32d/artifacts/bd5nly0b_tastes%20good%20does%20good%20%20group%20logo%20%20%287%29.png";

const SignupPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Signup failed');
      }

      const data = await response.json();
      login(data.user, data.session_token);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="signup-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-background-paper rounded-2xl shadow-xl p-8 border border-primary/20">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img 
                src={BRAND_LOGO} 
                alt="Cakery Wonderland" 
                className="h-20 w-auto mx-auto hover:scale-105 transition-transform duration-300"
              />
            </Link>
            <h1 className="text-3xl font-heading font-bold text-white mb-2" data-testid="signup-title">Create Account</h1>
            <p className="text-text-secondary font-body" data-testid="signup-subtitle">Join our wonderland community</p>
          </div>

          <form onSubmit={handleSignup} className="space-y-6" data-testid="signup-form">
            <div>
              <label className="block text-sm font-medium text-white mb-2 font-body" htmlFor="name">
                Name
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="pl-10 bg-background-elevated border-primary/20 text-white placeholder:text-text-muted rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2 font-body" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="pl-10 bg-background-elevated border-primary/20 text-white placeholder:text-text-muted rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2 font-body" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  className="pl-10 bg-background-elevated border-primary/20 text-white placeholder:text-text-muted rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
              <p className="mt-1 text-xs text-text-muted font-body">Must be at least 6 characters</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-hover text-white rounded-full py-3 text-lg shadow-lg shadow-primary/30 hover:shadow-primary/50 button-primary"
              data-testid="signup-submit-button"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-background-paper text-text-muted font-body">Or continue with</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleSignup}
              type="button"
              variant="outline"
              className="w-full mt-4 border-2 border-white/20 text-white hover:bg-white/5 hover:border-primary rounded-full py-3"
              data-testid="google-signup-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </div>

          <p className="mt-6 text-center text-text-secondary font-body" data-testid="login-link-text">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:text-primary-hover font-medium" data-testid="login-link">
              Login
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SignupPage;
